import { ResolvedApiSubwalletBalance } from '@api/interfaces';
import { groupBy, isBalanceLocked } from '@shared/utilities';

/**
 * Splits balances into one unlocked card per subwallet plus a card per locked program year.
 *
 * The split is on lock state, not program year: a promotion's points are never locked but sit on
 * the same program year as the program points beside them (RVC's preload), so grouping by year
 * alone would put locked and unlocked points on one card and mark the whole thing locked.
 *
 * Shared by the locked cards above the fold and the available list below it, so the two renders
 * cannot disagree about what a card is.
 */
export const groupWalletBalances = (
  balances: ResolvedApiSubwalletBalance[] | undefined,
): ResolvedApiSubwalletBalance[][] => {
  if (!balances) {
    return [];
  }
  const byOwner = (isManufacturer: boolean) => balances
    .filter((balance) => !!balance.manufacturerId === isManufacturer)
    .sort((a, b) => a.subwallet.name.localeCompare(b.subwallet.name));

  const group = (scoped: ResolvedApiSubwalletBalance[]) => Object.values(
    groupBy(scoped, (balance) => (
      isBalanceLocked(balance)
        ? `${balance.subwalletId}:locked:${balance.rewardsProgramId}`
        : `${balance.subwalletId}:unlocked`
    )),
  ).sort((a, b) => (
    a[0].subwallet.name.localeCompare(b[0].subwallet.name)
    || Number(isBalanceLocked(a[0])) - Number(isBalanceLocked(b[0]))
  ));

  // Retailer wallets before manufacturer ones.
  return [...group(byOwner(false)), ...group(byOwner(true))];
};

/**
 * The program year a card is named for, or null when it should keep its plain subwallet name.
 *
 * Only program-earned points in the retailer's rewards program wallet are named for their year -
 * that is what a lock applies to, so the year is what tells those cards apart. A promotion's
 * points belong to no program year even when they land in the program wallet (RVC's preload does),
 * so they keep the plain name, as do other wallets and any card spanning more than one year.
 */
export const programYearForGroup = (
  group: ResolvedApiSubwalletBalance[],
): number | null => {
  const [balance] = group;
  const isProgramSubwallet = (
    balance.subwallet.retailer?.programSubwalletId === balance.subwalletId
  );
  const years = new Set(
    group.map((item) => item.rewardsProgramYear).filter((year): year is number => !!year),
  );
  const isPromotionFunded = group.some((item) => item.promotionId);
  if (!isProgramSubwallet || isPromotionFunded || years.size !== 1) {
    return null;
  }
  return [...years][0];
};
